import { useEffect, useState, type JSX } from 'react';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { useT } from '../i18n/index.js';
import { api } from '../net/api.js';
import { useApp } from '../state/store.js';
import { Screen } from './parts/Screen.js';
import './FairPlay.css';

interface Fairness {
  commit: string;
  seed: string | null;
  algorithm: string;
  verified: boolean | null;
}

/**
 * The commit-reveal verifier (PRD §8). The point is that the player does the
 * check, not us: the hash runs in their browser via crypto.subtle, on the two
 * strings the server published. If our claim were false, this screen is where
 * it would break.
 */
export function FairPlay({ matchId }: { matchId: string }): JSX.Element {
  const setToast = useApp((s) => s.setToast);
  const t = useT();
  const [data, setData] = useState<Fairness | null>(null);
  const [failed, setFailed] = useState(false);
  const [computed, setComputed] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .fairness(matchId)
      .then((r) => alive && setData(r))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [matchId]);

  const check = () => {
    const seed = data?.seed;
    if (!seed) return;
    sha256Hex(seed)
      .then(setComputed)
      .catch(() => setToast(t('results.fair.nohash'), 'warn'));
  };

  const match = computed !== null && data !== null ? computed === data.commit.toLowerCase() : null;

  return (
    <Screen title={t('results.fair.title')} sub={t('results.fair.sub', { id: matchId.slice(0, 8) })}>
      <section className="s-panel">
        <div className="s-panel__head">
          <h2>{t('results.fair.head')}</h2>
        </div>
        <div className="fair__steps">
          <div className="fair__step">
            <p>{t('results.fair.step1')}</p>
          </div>
          <div className="fair__step">
            <p>{t('results.fair.step2')}</p>
          </div>
          <div className="fair__step">
            <p>{t('results.fair.step3')}</p>
          </div>
        </div>
      </section>

      {failed ? (
        <div className="s-empty">
          <b>{t('results.fair.error.title')}</b>
          <span>{t('results.fair.error.body')}</span>
        </div>
      ) : !data ? (
        <p className="s-note">{t('results.fair.loading')}</p>
      ) : (
        <section className="s-panel">
          <div className="fair__value">
            <b>{t('results.fair.commit', { algo: data.algorithm })}</b>
            <span className="s-mono">{data.commit}</span>
          </div>

          <div className="fair__value">
            <b>{t('results.fair.seed')}</b>
            <span className="s-mono">{data.seed ?? t('results.fair.seed.pending')}</span>
          </div>

          <div className="s-actions">
            <Button variant="primary" disabled={!data.seed} onClick={check}>
              {t('results.fair.check')}
            </Button>
            {data.verified === false ? (
              <span className="s-tag s-tag--warn">{t('results.fair.serverfail')}</span>
            ) : null}
          </div>

          {computed !== null ? (
            <>
              <div className="fair__value">
                <b>{t('results.fair.computed')}</b>
                <span className="s-mono">{computed}</span>
              </div>
              <p className="fair__verdict" data-ok={match}>
                <Icon name={match ? 'responsible' : 'signal'} size={18} />
                <span>{match ? t('results.fair.match') : t('results.fair.mismatch')}</span>
              </p>
            </>
          ) : null}
        </section>
      )}
    </Screen>
  );
}

/** Same function the server used, run on the player's own machine. */
async function sha256Hex(seed: string): Promise<string> {
  const bytes = new TextEncoder().encode(seed);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
